import { FLEET_METRICS_TIME_ZONE, type FleetMetricBucket } from '@immo/api-contract';

/**
 * The period the Metriken page covers, on the operators' calendar
 * (Europe/Berlin — the zone the series endpoint buckets in). A running period
 * stays running: the current week is never projected to seven days.
 *
 * Calendar arithmetic runs on civil dates — a `Date` at UTC midnight that
 * stands for a Berlin calendar day — and only the final window is turned into
 * instants, so a DST switch moves an instant, never a day.
 */
export const PERIOD_SCALES = ['day', 'week', 'month', 'thirteenWeeks'] as const;

export type PeriodScale = (typeof PERIOD_SCALES)[number];

/** `day`: `YYYY-MM-DD` · `week`: `YYYY-Www` · `month`: `YYYY-MM` · `thirteenWeeks`: its last week, `YYYY-Www`. */
export type Period = { readonly scale: PeriodScale; readonly key: string };

export type PeriodWindow = { readonly from: Date; readonly to: Date };

const DAY_MS = 86_400_000;
const WEEK_DAYS = 7;
const THIRTEEN = 13;
const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const WEEK_KEY = /^(\d{4})-W(\d{2})$/;
const MONTH_KEY = /^(\d{4})-(\d{2})$/;

export const PERIOD_BUCKET: Readonly<Record<PeriodScale, FleetMetricBucket>> = {
  day: 'hour',
  week: 'day',
  month: 'day',
  thirteenWeeks: 'week',
};

export function dayKey(civil: Date): string {
  return civil.toISOString().slice(0, 10);
}

/** `YYYY-Www` for the ISO week holding a civil date — the week's Thursday names its year. */
export function isoWeekKey(civil: Date): string {
  const weekday = (civil.getUTCDay() + 6) % WEEK_DAYS;
  const thursday = new Date(civil.getTime() + (3 - weekday) * DAY_MS);
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  const week = Math.floor((thursday.getTime() - yearStart) / DAY_MS / WEEK_DAYS) + 1;

  return `${thursday.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

/** The Monday that opens an ISO week, as a civil date. */
export function weekStart(key: string): Date {
  const match = WEEK_KEY.exec(key);

  if (!match) throw new Error(`not an ISO week: ${key}`);

  const year = Number(match[1]);
  const week = Number(match[2]);
  // 4 January is always in week 1.
  const jan4 = Date.UTC(year, 0, 4);
  const jan4Weekday = (new Date(jan4).getUTCDay() + 6) % WEEK_DAYS;

  return new Date(jan4 - jan4Weekday * DAY_MS + (week - 1) * WEEK_DAYS * DAY_MS);
}

/** The Berlin calendar day an instant falls on, as a civil date. */
export function civilToday(now: Date, timeZone: string = FLEET_METRICS_TIME_ZONE): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => Number(parts.find((entry) => entry.type === type)?.value);

  return new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
}

/** How far the zone is ahead of UTC at an instant, in milliseconds. */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const part = (type: string) => Number(parts.find((entry) => entry.type === type)?.value);
  const asUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));

  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Midnight of a civil date in the zone, as an instant. */
export function zonedMidnight(civil: Date, timeZone: string = FLEET_METRICS_TIME_ZONE): Date {
  const guess = civil.getTime() - zoneOffsetMs(civil, timeZone);
  const settled = civil.getTime() - zoneOffsetMs(new Date(guess), timeZone);

  return new Date(settled);
}

function addMonths(civil: Date, months: number): Date {
  return new Date(Date.UTC(civil.getUTCFullYear(), civil.getUTCMonth() + months, 1));
}

function monthStart(key: string): Date {
  const match = MONTH_KEY.exec(key);

  if (!match) throw new Error(`not a month: ${key}`);

  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
}

/** The civil days a period covers: `[first, end)`. */
export function periodDays(period: Period): { readonly first: Date; readonly end: Date } {
  switch (period.scale) {
    case 'day': {
      const first = new Date(`${period.key}T00:00:00Z`);

      return { first, end: new Date(first.getTime() + DAY_MS) };
    }

    case 'week': {
      const first = weekStart(period.key);

      return { first, end: new Date(first.getTime() + WEEK_DAYS * DAY_MS) };
    }

    case 'month': {
      const first = monthStart(period.key);

      return { first, end: addMonths(first, 1) };
    }

    case 'thirteenWeeks': {
      const last = weekStart(period.key);

      return {
        first: new Date(last.getTime() - (THIRTEEN - 1) * WEEK_DAYS * DAY_MS),
        end: new Date(last.getTime() + WEEK_DAYS * DAY_MS),
      };
    }
  }
}

export function periodWindow(period: Period): PeriodWindow {
  const { first, end } = periodDays(period);

  return { from: zonedMidnight(first), to: zonedMidnight(end) };
}

export function currentPeriod(scale: PeriodScale, now: Date = new Date()): Period {
  const today = civilToday(now);

  switch (scale) {
    case 'day':
      return { scale, key: dayKey(today) };
    case 'month':
      return { scale, key: dayKey(today).slice(0, 7) };
    default:
      return { scale, key: isoWeekKey(today) };
  }
}

export function shiftPeriod(period: Period, steps: number): Period {
  switch (period.scale) {
    case 'day':
      return { scale: 'day', key: dayKey(new Date(Date.parse(`${period.key}T00:00:00Z`) + steps * DAY_MS)) };
    case 'week':
      return { scale: 'week', key: isoWeekKey(new Date(weekStart(period.key).getTime() + steps * WEEK_DAYS * DAY_MS)) };
    case 'month':
      return { scale: 'month', key: dayKey(addMonths(monthStart(period.key), steps)).slice(0, 7) };
    case 'thirteenWeeks':
      return {
        scale: 'thirteenWeeks',
        key: isoWeekKey(new Date(weekStart(period.key).getTime() + steps * THIRTEEN * WEEK_DAYS * DAY_MS)),
      };
  }
}

function isValidKey(scale: PeriodScale, key: string): boolean {
  switch (scale) {
    case 'day':
      return DAY_KEY.test(key) && dayKey(new Date(`${key}T00:00:00Z`)) === key;
    case 'month': {
      const match = MONTH_KEY.exec(key);

      return match !== null && Number(match[2]) >= 1 && Number(match[2]) <= 12;
    }

    default:
      return WEEK_KEY.test(key) && isoWeekKey(weekStart(key)) === key;
  }
}

/** `?scale=…&at=…`; anything unreadable falls back to the current week. */
export function periodFromSearch(params: URLSearchParams, now: Date = new Date()): Period {
  const scale = params.get('scale');
  const key = params.get('at');

  if (!PERIOD_SCALES.includes(scale as PeriodScale)) return currentPeriod('week', now);

  const typed = scale as PeriodScale;

  if (key === null || !isValidKey(typed, key)) return currentPeriod(typed, now);

  return { scale: typed, key };
}

/** Whether the period after this one has started — the forward arrow is off in the future. */
export function hasNext(period: Period, now: Date = new Date()): boolean {
  return periodWindow(shiftPeriod(period, 1)).from.getTime() <= now.getTime();
}

/** Whether a run can lie before this period — the back arrow stops at the ledger's first run. */
export function hasPrevious(period: Period, firstRunAt: Date | null): boolean {
  return firstRunAt !== null && firstRunAt.getTime() < periodWindow(period).from.getTime();
}
